import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import useStore from '@store/store';
import { MessageInterface, PromptTurn } from '@type/chat';
import { scrollMessageIntoViewById } from '@utils/chatScrollCommands';
import {
  resolveInitialActiveTurn,
  resolveProgrammaticScrollState,
  resolveRestoreAction,
  type ProgrammaticScrollState,
} from '@utils/chatScrollMemoryQueries';
import {
  areChatViewStatesEqual,
  buildChatViewStateSnapshot,
} from '@utils/chatScrollMemorySnapshot';
import { debug } from '@utils/debug';
import {
  findActiveTurnStartMessageId,
  findTopVisibleMessageId,
} from '@utils/chatNavigation';

interface UseChatScrollMemoryProps {
  chatId: string | undefined;
  messages: MessageInterface[];
  turns: PromptTurn[];
  containerRef: MutableRefObject<HTMLDivElement | null>;
  initialActiveTurnStartMessageId: string | undefined;
}

interface UseChatScrollMemoryReturn {
  activeTurnStartMessageId: string | undefined;
  scrollToMessageId: (messageId: string, behavior?: ScrollBehavior) => boolean;
  saveCurrentViewState: () => void;
}

const LOG_PREFIX = '[useChatScrollMemory]';

export const useChatScrollMemory = ({
  chatId,
  messages,
  turns,
  containerRef,
  initialActiveTurnStartMessageId,
}: UseChatScrollMemoryProps): UseChatScrollMemoryReturn => {
  const [activeTurnStartMessageId, setActiveTurnStartMessageId] = useState<string | undefined>(
    initialActiveTurnStartMessageId
  );

  const previousChatIdRef = useRef<string | undefined>(undefined);
  const activeTurnStartMessageIdRef = useRef<string | undefined>(initialActiveTurnStartMessageId);
  const turnsRef = useRef<PromptTurn[]>(turns);
  const programmaticScrollRef = useRef<ProgrammaticScrollState | undefined>(undefined);

  const setTrackedActiveTurnStartMessageId = useCallback((messageId: string | undefined) => {
    setActiveTurnStartMessageId(messageId);
    activeTurnStartMessageIdRef.current = messageId;
  }, []);

  const syncActiveTurnToViewport = useCallback(
    (container: HTMLDivElement | null, fallbackMessageId?: string): string | undefined => {
      const nextActiveTurnStartMessageId = findActiveTurnStartMessageId(
        container,
        turnsRef.current,
        fallbackMessageId
      );

      if (nextActiveTurnStartMessageId !== activeTurnStartMessageIdRef.current) {
        debug.log('chat', `${LOG_PREFIX} syncing active turn to viewport`, {
          chatId,
          previousActiveTurnStartMessageId: activeTurnStartMessageIdRef.current,
          nextActiveTurnStartMessageId,
          fallbackMessageId,
        });
        setTrackedActiveTurnStartMessageId(nextActiveTurnStartMessageId);
      }

      return nextActiveTurnStartMessageId;
    },
    [chatId, setTrackedActiveTurnStartMessageId]
  );

  const scrollMessageIntoView = useCallback(
    (
      messageId: string,
      behavior: ScrollBehavior = 'smooth'
    ): { didScroll: boolean; targetScrollTop?: number } => {
      const previousScrollTop = containerRef.current?.scrollTop;
      const result = scrollMessageIntoViewById({
        container: containerRef.current,
        messageId,
        behavior,
      });

      if (!result.didScroll) {
        debug.log('chat', `${LOG_PREFIX} scrollToMessageId failed`, {
          chatId,
          messageId,
          behavior,
          hasContainer: result.hasContainer,
          hasElement: result.hasElement,
          renderedMessageCount: result.renderedMessageCount,
        });
        return { didScroll: false };
      }

      debug.log('chat', `${LOG_PREFIX} scrollToMessageId success`, {
        chatId,
        messageId,
        behavior,
        previousScrollTop,
        targetScrollTop: result.targetScrollTop,
      });

      return {
        didScroll: true,
        targetScrollTop: result.targetScrollTop,
      };
    },
    [chatId, containerRef]
  );

  useEffect(() => {
    activeTurnStartMessageIdRef.current = activeTurnStartMessageId;
  }, [activeTurnStartMessageId]);

  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  const saveCurrentViewState = useCallback(() => {
    if (!chatId) {
      debug.log('chat', `${LOG_PREFIX} save skipped: no active chatId`);
      return;
    }

    const container = containerRef.current;

    if (!container) {
      debug.log('chat', `${LOG_PREFIX} save skipped: container not ready`, { chatId });
      return;
    }

    const anchorMessageId = findTopVisibleMessageId(container);
    const nextActiveTurnStartMessageId = findActiveTurnStartMessageId(
      container,
      turnsRef.current,
      activeTurnStartMessageIdRef.current
    );

    const { chats, setChats } = useStore.getState();
    const chatIndex = chats.findIndex((chat) => chat.id === chatId);

    if (chatIndex === -1) {
      debug.log('chat', `${LOG_PREFIX} save skipped: chat not found`, { chatId });
      return;
    }

    const currentChat = chats[chatIndex];

    if (!currentChat) {
      debug.log('chat', `${LOG_PREFIX} save skipped: chat entry missing`, { chatId, chatIndex });
      return;
    }

    const nextViewState = buildChatViewStateSnapshot({
      scrollTop: container.scrollTop,
      anchorMessageId,
      activeTurnStartMessageId: nextActiveTurnStartMessageId,
    });

    const currentViewState = currentChat.viewState;
    if (areChatViewStatesEqual(currentViewState, nextViewState)) {
      debug.log('chat', `${LOG_PREFIX} save skipped: view state unchanged`, {
        chatId,
        nextViewState,
      });
      return;
    }

    const updatedChats = [...chats];
    updatedChats[chatIndex] = {
      ...currentChat,
      viewState: nextViewState,
    };

    debug.log('chat', `${LOG_PREFIX} saving view state`, {
      chatId,
      previousViewState: currentViewState,
      nextViewState,
      scrollHeight: container.scrollHeight,
      clientHeight: container.clientHeight,
      turnCount: turnsRef.current.length,
    });

    setChats(updatedChats);
  }, [chatId, containerRef]);

  const scrollToMessageId = useCallback(
    (messageId: string, behavior: ScrollBehavior = 'smooth'): boolean => {
      const { didScroll, targetScrollTop } = scrollMessageIntoView(messageId, behavior);

      if (!didScroll || targetScrollTop === undefined) {
        return false;
      }

      if (behavior === 'smooth') {
        programmaticScrollRef.current = {
          messageId,
          targetScrollTop,
        };
        debug.log('chat', `${LOG_PREFIX} locking active turn during programmatic scroll`, {
          chatId,
          messageId,
          targetScrollTop,
        });
      } else {
        programmaticScrollRef.current = undefined;
      }

      setTrackedActiveTurnStartMessageId(messageId);
      return true;
    },
    [chatId, scrollMessageIntoView, setTrackedActiveTurnStartMessageId]
  );

  useEffect(() => {
    if (!chatId) {
      debug.log('chat', `${LOG_PREFIX} clearing active turn state: no chat selected`);
      previousChatIdRef.current = undefined;
      setActiveTurnStartMessageId(undefined);
      activeTurnStartMessageIdRef.current = undefined;
      return;
    }

    if (previousChatIdRef.current === chatId) {
      debug.log('chat', `${LOG_PREFIX} restore skipped: chatId unchanged`, { chatId });
      return;
    }

    debug.log('chat', `${LOG_PREFIX} chat switch detected`, {
      previousChatId: previousChatIdRef.current,
      nextChatId: chatId,
      turnCount: turnsRef.current.length,
      initialActiveTurnStartMessageId,
    });

    previousChatIdRef.current = chatId;

    const currentChat = useStore.getState().chats.find((chat) => chat.id === chatId);
    const savedViewState = currentChat?.viewState;
    const restoreAction = resolveRestoreAction({ savedViewState, turns: turnsRef.current });
    const defaultActiveTurnStartMessageId = resolveInitialActiveTurn({ savedViewState });

    programmaticScrollRef.current = undefined;
    setTrackedActiveTurnStartMessageId(defaultActiveTurnStartMessageId);

    debug.log('chat', `${LOG_PREFIX} prepared restore state`, {
      chatId,
      savedViewState,
      defaultActiveTurnStartMessageId,
      restoreAction,
    });

    const restoreHandle = window.requestAnimationFrame(() => {
      const container = containerRef.current;

      if (!container) {
        debug.log('chat', `${LOG_PREFIX} restore aborted: container not ready`, { chatId });
        return;
      }

      switch (restoreAction.type) {
        case 'sync-from-viewport': {
          debug.log('chat', `${LOG_PREFIX} restore skipped: no saved view state`, { chatId });
          const viewportActiveTurnStartMessageId = syncActiveTurnToViewport(
            container,
            restoreAction.fallbackActiveTurnStartMessageId
          );
          debug.log('chat', `${LOG_PREFIX} initialized active turn from viewport`, {
            chatId,
            viewportActiveTurnStartMessageId,
          });
          return;
        }
        case 'restore-scroll-top': {
          debug.log('chat', `${LOG_PREFIX} restoring scrollTop`, {
            chatId,
            scrollTop: restoreAction.scrollTop,
          });
          container.scrollTo({ top: restoreAction.scrollTop, behavior: 'auto' });
          syncActiveTurnToViewport(container, restoreAction.fallbackActiveTurnStartMessageId);
          return;
        }
        case 'restore-anchor': {
          const { didScroll } = scrollMessageIntoView(restoreAction.anchorMessageId, 'auto');
          const restoredActiveTurnStartMessageId = syncActiveTurnToViewport(
            container,
            restoreAction.fallbackActiveTurnStartMessageId
          );
          debug.log('chat', `${LOG_PREFIX} restoring anchor`, {
            chatId,
            anchorMessageId: restoreAction.anchorMessageId,
            didRestore: didScroll,
            restoredActiveTurnStartMessageId,
          });
          return;
        }
        case 'sync-from-saved-active-turn':
        default:
          debug.log('chat', `${LOG_PREFIX} restore skipped: saved state had no usable anchor`, {
            chatId,
            savedViewState,
          });
          syncActiveTurnToViewport(container, restoreAction.fallbackActiveTurnStartMessageId);
      }
    });

    return () => {
      debug.log('chat', `${LOG_PREFIX} cancelling pending restore frame`, { chatId });
      window.cancelAnimationFrame(restoreHandle);
    };
  }, [
    chatId,
    containerRef,
    initialActiveTurnStartMessageId,
    scrollMessageIntoView,
    setTrackedActiveTurnStartMessageId,
    syncActiveTurnToViewport,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !chatId) {
      debug.log('chat', `${LOG_PREFIX} scroll listener not attached`, {
        chatId,
        hasContainer: Boolean(container),
      });
      return undefined;
    }

    let frameId = 0;

    debug.log('chat', `${LOG_PREFIX} attaching scroll listener`, { chatId });

    const handleScroll = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        const programmaticScrollResolution = resolveProgrammaticScrollState({
          programmaticScrollState: programmaticScrollRef.current,
          currentScrollTop: container.scrollTop,
        });

        if (programmaticScrollResolution.type === 'keep-lock') {
            debug.log('chat', `${LOG_PREFIX} ignoring scroll during programmatic navigation`, {
              chatId,
              currentScrollTop: container.scrollTop,
              targetScrollTop: programmaticScrollResolution.targetScrollTop,
              targetMessageId: programmaticScrollResolution.messageId,
            });
            return;
        }

        if (programmaticScrollResolution.type === 'release-lock') {
          debug.log('chat', `${LOG_PREFIX} programmatic scroll settled`, {
            chatId,
            targetMessageId: programmaticScrollResolution.messageId,
            currentScrollTop: container.scrollTop,
          });
          programmaticScrollRef.current = undefined;
          setTrackedActiveTurnStartMessageId(programmaticScrollResolution.messageId);
          return;
        }

        const nextActiveTurnStartMessageId = findActiveTurnStartMessageId(
          container,
          turnsRef.current,
          activeTurnStartMessageIdRef.current
        );

        if (
          nextActiveTurnStartMessageId &&
          nextActiveTurnStartMessageId !== activeTurnStartMessageIdRef.current
        ) {
          debug.log('chat', `${LOG_PREFIX} active turn changed from scroll`, {
            chatId,
            previousActiveTurnStartMessageId: activeTurnStartMessageIdRef.current,
            nextActiveTurnStartMessageId,
          });
          setTrackedActiveTurnStartMessageId(nextActiveTurnStartMessageId);
        }
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      debug.log('chat', `${LOG_PREFIX} detaching scroll listener`, { chatId });
      container.removeEventListener('scroll', handleScroll);
    };
  }, [chatId, containerRef, setTrackedActiveTurnStartMessageId]);

  useEffect(() => {
    const handlePageHide = () => {
      debug.log('chat', `${LOG_PREFIX} pagehide: persisting current view state`, { chatId });
      saveCurrentViewState();
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [saveCurrentViewState]);

  useEffect(() => {
    return () => {
      debug.log('chat', `${LOG_PREFIX} cleanup: persisting current view state`, { chatId });
      saveCurrentViewState();
    };
  }, [chatId, saveCurrentViewState]);

  useEffect(() => {
    if (messages.length === 0) {
      debug.log('chat', `${LOG_PREFIX} resetting active turn: chat has no messages`, { chatId });
      programmaticScrollRef.current = undefined;
      setTrackedActiveTurnStartMessageId(undefined);
    }
  }, [chatId, messages.length, setTrackedActiveTurnStartMessageId]);

  return {
    activeTurnStartMessageId,
    scrollToMessageId,
    saveCurrentViewState,
  };
};